/**
 * The node card (08 §2.2, 04 §2.2): everything the player may know about a node before he
 * commits - archetype, terrain, light, weather, modifiers, reward, Threat Rating. An unscouted
 * node gives up its archetype (or kind) and nothing else. Every name comes from `ctx.describe`.
 *
 * A mission's card also says what kind of job it is - its category, its one-line summary - and carries the
 * small tags its FACTS give it (TIMED, NO DROP, POD A, NO RECALL, QUIET): what a player weighing a route
 * wants to know before he reads a briefing. They follow from the archetype, so an unscouted node shows them too.
 */

import './rail.css';
import type { NodeKind, TheatreNode } from '../../../meta/types';
import { archetypeInfo } from '../archetypes';
import type { Ctx } from '../context';
import { box, el } from '../dom';
import { factChips } from '../missionTags';
import { multiplier, RP_MEANING } from '../text';
import { segmentGauge } from '../widgets/gauge';

/** The state in words: the node's colour is never the only signal (08 §4). */
export const STATE_LABEL: Readonly<Record<TheatreNode['state'], string>> = {
  hidden: 'UNSCOUTED',
  scouted: 'SCOUTED',
  open: 'OPEN - REACHABLE NOW',
  done: 'DONE',
  failed: 'FAILED',
  skipped: 'BYPASSED',
};

/** Missions and rescues are flown; everything else resolves in a dialog. */
export function isMissionKind(kind: NodeKind): boolean {
  return kind === 'mission' || kind === 'rescue';
}

/** What to call the node: the archetype for a plain mission, the kind for everything else. */
export function nodeName(ctx: Ctx, node: TheatreNode): string {
  if (node.kind === 'mission' && node.params !== null) return ctx.describe(node.params.archetype).name;
  return ctx.describe(node.kind).name;
}

export interface NodeCard {
  readonly el: HTMLElement;
  /** `message` is the line under the card: what Enter does, or why it does nothing. */
  show(node: TheatreNode | null, message?: string): void;
}

function fact(key: string, value: string, hint?: string): HTMLElement {
  const row = box('menu-card-fact', el('span', 'menu-card-key', key), el('span', 'menu-paper', value));
  if (hint !== undefined) row.title = hint;
  return row;
}

function described(ctx: Ctx, heading: string, ids: readonly string[]): HTMLElement[] {
  if (ids.length === 0) return [];
  const out: HTMLElement[] = [el('div', 'menu-card-key', heading)];
  for (const id of ids) {
    const d = ctx.describe(id);
    const row = el('div', 'menu-card-mod menu-prose');
    row.append(el('span', 'menu-warn', d.name));
    if (d.text !== '') row.append(el('span', 'menu-dim', ` ${d.text}`));
    out.push(row);
  }
  return out;
}

export function nodeCard(ctx: Ctx): NodeCard {
  const root = el('div', 'menu-card menu-col');
  root.setAttribute('aria-live', 'polite');

  const show = (node: TheatreNode | null, message = ''): void => {
    if (node === null) {
      root.replaceChildren(el('div', 'menu-dim menu-prose', 'MOVE TO A NODE TO READ ITS CARD.'));
      return;
    }
    const tags = el('div', 'menu-card-tags menu-row menu-row-wrap');
    if (node.keystone) tags.append(el('span', 'menu-chip menu-chip-amber', 'KEYSTONE'));
    if (node.variant) tags.append(el('span', 'menu-chip menu-chip-dim', 'VARIANT'));
    if (node.tagRecovery !== undefined) tags.append(el('span', 'menu-chip menu-chip-amber', 'TAG RECOVERY'));
    const p = node.params;
    if (p !== null) tags.append(...factChips(ctx.describe, p.archetype));
    tags.append(el('span', `menu-chip menu-chip-${stateTone(node.state)}`, STATE_LABEL[node.state]));
    const parts: HTMLElement[] = [el('div', 'menu-card-name', nodeName(ctx, node)), tags];
    if (p !== null) {
      parts.push(fact('CATEGORY', ctx.describe(`category:${archetypeInfo(p.archetype).category}`).name));
    }

    if (node.state === 'hidden') {
      parts.push(el('div', 'menu-dim menu-prose', 'UNSCOUTED: NOTHING ELSE IS KNOWN UNTIL THE COMPANY IS CLOSER.'));
    } else {
      if (node.kind !== 'mission') {
        const text = ctx.describe(node.kind).text;
        if (text !== '') parts.push(el('div', 'menu-prose menu-paper', text));
      }
      if (p !== null) {
        if (node.kind !== 'mission') parts.push(fact('MISSION', ctx.describe(p.archetype).name));
        const summary = ctx.describe(p.archetype).text;
        if (summary !== '') parts.push(el('div', 'menu-card-summary menu-prose menu-paper', summary));
        const threat = segmentGauge('THREAT', 9, 7);
        threat.set(p.threat);
        parts.push(
          fact('TERRAIN', ctx.describe(p.biome).name),
          fact('LIGHT', ctx.describe(p.timeOfDay).name),
          fact('WEATHER', ctx.describe(p.weather).name),
          threat.el,
        );
      }
      if (node.rewardRp > 0 || p !== null) {
        const bonus = node.rewardMultiplier !== 1 ? ` ${multiplier(node.rewardMultiplier)}` : '';
        parts.push(fact('REWARD', `RP ${node.rewardRp}${bonus}`, RP_MEANING));
      }
      if (node.tagRecovery !== undefined) {
        parts.push(
          el('div', 'menu-card-tagline menu-prose', `${node.tagRecovery.name.toUpperCase()}'S TAGS LIE ON THIS GROUND.`
            + ' OPTIONAL: BRING THEM HOME AND ONE OF HIS PERKS CAN BE TAUGHT.'),
        );
      }
      if (p !== null) {
        parts.push(...described(ctx, 'MODIFIERS', p.modifiers), ...described(ctx, 'INTEL HELD', p.intel));
        if (p.modifiers.length === 0) parts.push(el('div', 'menu-dim menu-small', 'NO MODIFIERS'));
      }
    }
    if (message !== '') parts.push(el('div', 'menu-card-message', message));
    root.replaceChildren(...parts);
  };

  show(null);
  return { el: root, show };
}

function stateTone(state: TheatreNode['state']): 'green' | 'amber' | 'red' | 'dim' {
  if (state === 'done') return 'green';
  if (state === 'failed') return 'red';
  return state === 'open' ? 'amber' : 'dim';
}
